import { Banknote, Percent, Receipt, Wallet } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import { formatIvaRate } from '@/shared/countries';
import type { Moneda } from './MoneyCell';
import type { InvoiceBreakdown } from '../lib/pagosCalc';
import { fmtPct } from '@/shared/api/controllers/mpCuotas';

/**
 * Left panel of the Pagos screen — desglose de la factura.
 *
 * Layout (top → bottom, after the latest QA redesign):
 *   1. Three stat tiles in one row: % de Factura · IVA 21% · IVA 10,5%.
 *      `% de Factura` is the only interactive cell (it's an input
 *      sized to look like its read-only siblings so the row reads as
 *      a unified "stat strip").
 *   2. Cálculo de factura — Monto, IVA, Total Factura.
 *   3. Cobro en efectivo (only when there's a cash portion).
 *   4. Monto Total a Cobrar — emerald headline; same height as the
 *      stat tiles above so the card doesn't taper visually.
 *
 * Removed from previous iterations and moved elsewhere:
 *   - The venta resumen (cliente / Nº / fecha / vendedor / moneda) now
 *     lives in the page toolbar as a header strip.
 *   - The "Cobertura de medios de pago" bar now sits at the top of
 *     the MediosDePagoTable so it visually attaches to the medios
 *     it's measuring.
 */
export function InvoiceBreakdownCard({
  porcentaje,
  onPorcentajeChange,
  onPorcentajeFocus,
  onPorcentajeBlur,
  breakdown,
  moneda,
  readOnly = false,
  usado = false,
}: {
  porcentaje: number;
  onPorcentajeChange: (next: number) => void;
  /** Snapshot the current value so the input can be reverted if the
   *  user cancels an approval dialog opened on blur. */
  onPorcentajeFocus?: () => void;
  /** Commit hook — runs gates like the MX factura% approval threshold
   *  on blur (NOT on every keystroke, so typing "5" → "50" doesn't
   *  fire the dialog before the user reaches "50"). */
  onPorcentajeBlur?: () => void;
  breakdown: InvoiceBreakdown;
  moneda: Moneda;
  readOnly?: boolean;
  /** MX Nova "horno usado" — true when the current porcentaje keeps
   *  `total_factura` below the cost+commission+IVA threshold. Renders
   *  a red pill next to the header so reviewers can see the state at
   *  a glance. AR + non-Nova MX ventas always pass `false`. The
   *  numeric threshold is intentionally NOT surfaced anywhere in this
   *  card — it leaks cost+comisión+IVA, which is hidden for now from
   *  every role. */
  usado?: boolean;
}) {
  const hasRecargo = breakdown.recargoMp > 0.005;
  const hasEfectivo = breakdown.montoCobroEfectivo > 0.005;

  return (
    <div className="flex min-h-0 flex-col gap-2.5 rounded-2xl border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          Detalle de la factura
        </h2>
        {usado && (
          <span
            className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700"
            title="Por debajo del costo contable + comisión + IVA"
          >
            ⚠ Usado
          </span>
        )}
      </div>

      {/* SECTION 1 — Desglose de IVA. Porcentaje (editable) + a tile
          per IVA rate present on the venta. Rates come from the
          country's `subtotalsByRate` (AR: 21 / 10,5; MX: 16 / 8).
          Sorted descending so the higher rate sits next to Porcentaje. */}
      {(() => {
        const rateEntries = Object.entries(breakdown.subtotalsByRate)
          .map(([k, v]) => [Number(k), v] as const)
          .sort((a, b) => b[0] - a[0]);
        const totalCols = 1 + Math.max(rateEntries.length, 2);
        return (
          <div
            className={cn(
              'grid grid-cols-2 gap-2',
              totalCols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-4',
            )}
          >
            <PorcentajeTile
              value={porcentaje}
              onChange={onPorcentajeChange}
              onFocus={onPorcentajeFocus}
              onBlur={onPorcentajeBlur}
              readOnly={readOnly}
            />
            {rateEntries.length === 0 ? (
              <StatTile label="Subtotal IVA" amount={0} moneda={moneda} />
            ) : (
              rateEntries.map(([rate, amount], i) => (
                <StatTile
                  key={rate}
                  label={`Subtotal IVA ${formatIvaRate(rate)}%`}
                  amount={amount}
                  moneda={moneda}
                  className={i === rateEntries.length - 1 ? 'col-span-2 sm:col-span-1' : ''}
                />
              ))
            )}
          </div>
        );
      })()}

      <div className="border-t border-border/60" />

      {/* SECTION 2 — Cálculo de factura. Monto + IVA = Total Factura,
          reading left-to-right on the addition. */}
      <div className={cn('grid grid-cols-1 gap-2', hasRecargo ? 'sm:grid-cols-4' : 'sm:grid-cols-3')}>
        <CalcCell
          label="Monto de Factura"
          value={breakdown.montoFactura}
          moneda={moneda}
        />
        <CalcCell
          label="IVA a Cobrar"
          value={breakdown.ivaACobrar}
          moneda={moneda}
        />
        {/* Recargo MercadoPago por cuotas: se suma al total con factura. */}
        {hasRecargo && (
          <CalcCell
            label={`Recargo MP · ${fmtPct(breakdown.recargoMpPct)}`}
            value={breakdown.recargoMp}
            moneda={moneda}
            tone="sky"
          />
        )}
        <CalcCell
          label="Total Factura"
          value={breakdown.totalFactura}
          moneda={moneda}
          emphasise
          icon={<Receipt className="h-3.5 w-3.5" aria-hidden />}
        />
      </div>

      <div className="border-t border-border/60" />

      {/* SECTION 3 — Total a cobrar. Cobro en Efectivo (when present)
          + Monto Total a Cobrar (emerald). The headline at the bottom
          of the card. */}
      <div
        className={cn(
          'grid gap-2',
          hasEfectivo ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
        )}
      >
        {hasEfectivo && (
          <SummaryTile
            label="Cobro en Efectivo"
            value={breakdown.montoCobroEfectivo}
            moneda={moneda}
            icon={<Banknote className="h-4 w-4" aria-hidden />}
          />
        )}
        <SummaryTile
          label={hasRecargo ? 'Monto Total a Cobrar · incl. recargo MP' : 'Monto Total a Cobrar'}
          value={breakdown.montoTotalCobro}
          moneda={moneda}
          emerald
          icon={<Wallet className="h-4 w-4" aria-hidden />}
        />
      </div>
    </div>
  );
}

function CalcCell({
  label,
  value,
  moneda,
  emphasise,
  icon,
  tone,
}: {
  label: string;
  value: number;
  moneda: Moneda;
  /** Last cell of the addition (Total Factura) — slightly heavier
   *  weight so the result reads as the row's outcome. */
  emphasise?: boolean;
  /** 'sky' = recargo MercadoPago (se distingue de la base de la venta). */
  tone?: 'sky';
  /** Optional Lucide icon rendered next to the label. */
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          'text-[10px] font-medium uppercase tracking-wide',
          tone === 'sky' ? 'text-sky-700' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'inline-flex items-center justify-between gap-1.5',
          tone === 'sky' ? 'text-sky-800' : 'text-foreground',
        )}
      >
        <span
          className={cn(
            'tabular-nums',
            emphasise ? 'text-base font-bold' : 'text-sm font-semibold',
          )}
        >
          {formatMoney(value, moneda)}
        </span>
        {icon ? (
          <span className="text-muted-foreground">{icon}</span>
        ) : null}
      </span>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  moneda,
  emerald,
  icon,
}: {
  label: string;
  value: number;
  moneda: Moneda;
  emerald?: boolean;
  /** Optional Lucide icon rendered next to the label. */
  icon?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-xl border px-3 py-2',
        emerald
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-transparent bg-secondary/60 text-foreground',
      )}
    >
      <span
        className={cn(
          'text-[10px] font-medium uppercase tracking-wide',
          emerald ? 'text-emerald-900/70' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      <span className="inline-flex items-center justify-between gap-1.5">
        <span
          className={cn(
            'tabular-nums',
            emerald ? 'text-base font-bold' : 'text-sm font-semibold',
          )}
        >
          {formatMoney(value, moneda)}
        </span>
        {icon ? (
          <span className={emerald ? 'text-emerald-700' : 'text-muted-foreground'}>
            {icon}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * % de Factura, styled to match `StatTile` so the three-tile strip
 * reads as a unified row of stats. Internally it's still an `<Input>`
 * so the user can tab in and type a new percentage.
 */
function PorcentajeTile({
  value,
  onChange,
  onFocus,
  onBlur,
  readOnly,
}: {
  value: number;
  onChange: (next: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  readOnly: boolean;
}) {
  return (
    <div className="rounded-xl bg-secondary/60 px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Porcentaje Factura
      </div>
      <div className="relative">
        <Input
          // type="text" + inputMode keeps React's controlled-input
          // synced with the canonical integer ("15", not "015") —
          // QA #9 fix. The visible Percent icon stands in for the
          // text "%" suffix.
          type="text"
          inputMode="numeric"
          maxLength={3}
          value={String(value)}
          disabled={readOnly}
          onFocus={(e) => {
            e.currentTarget.select();
            onFocus?.();
          }}
          onBlur={() => onBlur?.()}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            if (digits === '') {
              onChange(0);
              return;
            }
            const v = Number(digits);
            if (!Number.isFinite(v)) return;
            onChange(Math.max(0, Math.min(100, v)));
          }}
          // Borderless + transparent bg so it looks like a static
          // value until the user actually clicks/tabs into it.
          className="h-6 w-full border-0 bg-transparent p-0 pr-5 text-sm font-semibold tabular-nums shadow-none focus-visible:ring-0"
          aria-label="Porcentaje de Factura"
        />
        <Percent
          className="pointer-events-none absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
      </div>
    </div>
  );
}

function StatTile({
  label,
  amount,
  moneda,
  className,
}: {
  label: string;
  amount: number;
  moneda: Moneda;
  className?: string;
}) {
  return (
    <div className={cn('rounded-xl bg-secondary/60 px-3 py-2', className)}>
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-semibold tabular-nums text-foreground">
        {formatMoney(amount, moneda)}
      </div>
    </div>
  );
}

